import React, { useState } from 'react';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    OutlinedInput,
    makeStyles,
} from '@material-ui/core';
import { Block, Typography } from '@klp/design';
import { useTranslation } from 'react-i18next';
import { theme } from '@klp/design/Theme/theme';
import { useGetRegionerQuery } from '../services/rtkApi';
import type { SelectInputProps } from '@material-ui/core/Select/SelectInput';

const useStyles = makeStyles(() => ({
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
    text: {
        paddingLeft: 8,
    },
}));

interface RegionSelectorProps {
    region?: string;
    hasEditAccess: boolean;
    brukerIsLoading?: boolean;
    callbackChange?: (value: string) => void;
}

export default function RegionSelector({
    region,
    hasEditAccess,
    brukerIsLoading,
    callbackChange,
}: RegionSelectorProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [selectedRegion, setSelectedRegion] = useState<string>(region || '');

    const { data: regionerData = [], isLoading: regionerIsLoading } =
        useGetRegionerQuery();

    const handleRegionSelect: SelectInputProps['onChange'] = (event) => {
        const value = event.target.value as string;
        setSelectedRegion(value);
        if (callbackChange) {
            callbackChange(value);
        }
    };

    const isLoading = brukerIsLoading || regionerIsLoading;

    if (isLoading) return null;

    return (
        <>
            <Typography
                translation={
                    hasEditAccess
                        ? 'users.editUser.regionDescription'
                        : 'users.showUserInfo.regionDescription'
                }
            />

            <Block paddingBottom="tiny" paddingTop="tiny">
                <FormControl
                    className={classes.selector}
                    variant="outlined"
                    required={true}
                >
                    <InputLabel
                        className={classes.text}
                        id="region-select-label"
                    >
                        {hasEditAccess
                            ? t('users.editUser.assignRegion')
                            : t('users.showUserInfo.labelRegion')}
                    </InputLabel>
                    <Select
                        disabled={!hasEditAccess || isLoading}
                        labelId="region-select-label"
                        id="region-select"
                        value={selectedRegion}
                        onChange={handleRegionSelect}
                        input={<OutlinedInput label="Region" />}
                    >
                        {regionerData.map((name) => (
                            <MenuItem key={name} value={name}>
                                {t(`users.regionSelector.${name}`)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Block>
        </>
    );
}
